import { merge, pick, pickBy } from 'lodash';
import { useErrorBoundary } from 'react-error-boundary';
import { useQueryClient } from 'react-query';
import shortUUID from 'short-uuid';

import type {
    GetClassroomWithInstructorsQuery,
    GetLearningActivityQuery,
    UpdateClassroomV2Input,
    UpdateClassroomV2Mutation,
    UpdateClassroomV2MutationVariables,
} from '@/api/generated';
import {
    useClientLogMutation,
    useGetClassroomWithInstructorsQuery,
    useGetLearningActivityQuery,
    useListClassroomRelationshipsQuery,
    useUpdateClassroomV2Mutation,
} from '@/api/generated';
import { getRetryDelay, shouldRetryGetClassroom } from '@/api/hooks.utils';
import { useProvider } from '@/data/ProviderContext';
import { createLogMessage } from '@/utils/createLogMessage';
import { useDecodedParams } from '@/utils/paramHooks';
import { useAcceptInvitations } from '@/utils/useAcceptInvitations';

import type { UseQueryOptions, UseMutationOptions } from 'react-query';

export const useGetClassroom = (
    options?: UseQueryOptions<
        GetClassroomWithInstructorsQuery,
        unknown,
        GetClassroomWithInstructorsQuery
    >,
) => {
    const { showBoundary } = useErrorBoundary();
    const { classroomId = '' } = useDecodedParams();
    const { arn: providerArn } = useProvider();
    const acceptedInvitations = useAcceptInvitations() as null | any[];
    const queryClient = useQueryClient();

    const variables = {
        classroomId,
        providerArn,
        withStudentRoster: true,
        withSubProviderCapacity: true,
    };

    return {
        ...useGetClassroomWithInstructorsQuery(variables, {
            enabled: !!classroomId && !!providerArn,
            refetchOnWindowFocus: false,
            staleTime: 5000,
            retryDelay: getRetryDelay,
            retry: (retryNum, err: any) => {
                // NOTE: null acceptedInvitations means we're still waiting for that response so count it optimistically
                const hasAcceptedInvitations =
                    acceptedInvitations === null || acceptedInvitations?.length > 0;
                return shouldRetryGetClassroom({ retryNum, hasAcceptedInvitations, error: err });
            },
            onError: showBoundary,
        }),
        ...options,
        updateCache: ({
            classroom,
            studentRosterExclusion,
            studentRosterAddition,
        }: {
            classroom?: Omit<
                Partial<GetClassroomWithInstructorsQuery['getClassroom']['classroom']>,
                'locationData'
            > & {
                locationData?: Partial<
                    GetClassroomWithInstructorsQuery['getClassroom']['classroom']['locationData']
                >;
            };
            studentRosterExclusion?: string;
            studentRosterAddition?: Array<{ email: string }>;
        }) => {
            const original = queryClient.getQueryData([
                'GetClassroomWithInstructors',
                variables,
            ]) as GetClassroomWithInstructorsQuery;

            if (studentRosterExclusion && original.studentRoster) {
                original.studentRoster = {
                    classroomUsers: original.studentRoster.classroomUsers.filter(
                        (user) => user!.email !== studentRosterExclusion,
                    ),
                };
            }
            if (studentRosterAddition && original.studentRoster) {
                original.studentRoster = {
                    classroomUsers: [
                        ...original.studentRoster.classroomUsers,
                        ...studentRosterAddition.map((user) => ({
                            ...user,
                            hasAcceptedInvitation: false,
                        })),
                    ],
                };
            }

            queryClient.setQueryData(
                ['GetClassroomWithInstructors', variables],
                merge(original, { getClassroom: { classroom } }),
            );
        },
    };
};

export const useListClassroomInstructors = ({ enabled }: { enabled?: boolean } = {}) => {
    const { classroomId = '' } = useDecodedParams();
    const { arn: providerArn } = useProvider();
    return useListClassroomRelationshipsQuery(
        {
            input: { classroomId, providerArn, relationship: 'instructor' },
        },
        { enabled: !!classroomId && !!providerArn && enabled !== false },
    );
};

export const useListClassroomStudents = ({ enabled }: { enabled?: boolean } = {}) => {
    const { classroomId = '' } = useDecodedParams();
    const provider = useProvider();
    const providerArn = provider?.arn;
    return useListClassroomRelationshipsQuery(
        {
            input: { classroomId, providerArn, relationship: 'student' },
        },
        { enabled: !!classroomId && !!providerArn && enabled !== false },
    );
};

export const useUpdateClassroom = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateClassroomV2Mutation,
        TError,
        UpdateClassroomV2MutationVariables,
        TContext
    >,
) => {
    const { data: classData, updateCache } = useGetClassroom();
    const classroom = classData?.getClassroom.classroom;
    const mutation = useUpdateClassroomV2Mutation({
        ...options,
        onSuccess: (data, ...rest) => {
            const {
                langLocale,
                courseId,
                startsOn,
                endsOn,
                classCapacity,
                timezone,
                locationType,
                virtualUrl,
                addressLine1,
                addressLine2,
                city,
                state,
                country,
                postalCode,
            } = data.updateClassroomV2 ?? {};
            const classroomUpdates = pickBy(
                { langLocale, courseId, startsOn, endsOn, classCapacity },
                (v) => v !== undefined || v !== null,
            );
            const locationData = pickBy(
                {
                    timezone,
                    locationType,
                    virtualUrl,
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    country,
                    postalCode,
                },
                (v) => v !== undefined || v !== null,
            );

            updateCache({
                classroom: {
                    ...classroomUpdates,
                    locationData,
                },
            });
            if (options?.onSuccess) {
                options.onSuccess(data, ...rest);
            }
        },
    });
    return {
        ...mutation,
        isEnabled: !!classroom?.classroomId,
        mutate: (
            input: Partial<Omit<UpdateClassroomV2Input, 'classroomId' | 'clientRequestToken'>>,
        ) => {
            if (!classroom?.classroomId) return;
            return mutation.mutate({
                input: merge(
                    {
                        classroomId: classroom.classroomId,
                        classroom: {
                            ...pick(classroom, [
                                'providerArn',
                                'langLocale',
                                'courseId',
                                'startsOn',
                                'endsOn',
                                'accessType',
                            ]),
                            locationData: pick(classroom.locationData, [
                                'locationType',
                                'timezone',
                                'virtualUrl',
                            ]),
                        },

                        clientRequestToken: shortUUID.generate(),
                    },
                    input,
                ),
            });
        },
    };
};

export const useGetLearningActivity = (
    options?: UseQueryOptions<GetLearningActivityQuery, unknown, GetLearningActivityQuery>,
) => {
    const { data } = useGetClassroom();
    const { classroomId, providerArn, learningActivityID } = data?.getClassroom.classroom ?? {};
    const allInputsExist = [classroomId, providerArn, learningActivityID].every(
        (value) => value !== undefined && value !== null && value.length,
    );

    return useGetLearningActivityQuery(
        {
            input: {
                classroomId: classroomId ?? '',
                providerArn: providerArn ?? '',
                learningActivityID: learningActivityID ?? '',
            },
        },
        { ...options, enabled: allInputsExist },
    );
};

type ClientLogPayload = {
    type: 'AppClick' | string;
    metrics: {
        [metricName: string]: [number, string];
    };
    attributes: {
        [attributeKey: string]: string | number;
    };
};
export const useSendClientLog = () => {
    const { mutate } = useClientLogMutation();
    return {
        sendLog: (payload: ClientLogPayload) => {
            return createLogMessage({ payload }).then((body) =>
                mutate({ input: { body: JSON.stringify(body) } }),
            );
        },
        sendAppClickEvent: ({
            metricName,
            ...attributes
        }: {
            metricName: string;
        } & ClientLogPayload['attributes']) => {
            return createLogMessage({
                payload: { type: 'AppClick', metrics: { [metricName]: [1, 'Count'] }, attributes },
            })
                .then((body) => mutate({ input: { body: JSON.stringify(body) } }))
                .catch((_) => {
                    console.log('failed analytics call');
                });
        },
    };
};
